import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpDown, Car, ListFilter, PanelRightOpen, Search, WifiOff, X } from 'lucide-react';
import { EmptyState } from '@/components/common/empty-state';
import { VehicleRow } from '@/components/vehicles/vehicle-row';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FleetSnapshot } from '@/hooks/use-fleet';
import { STATUS_LABEL } from '@/lib/vehicle-status';
import { cn } from '@/lib/utils';
import { useUiStore, type VehicleSort } from '@/stores/ui-store';
import type { LiveStatus } from '@/types/api';

const SORT_LABEL: Record<VehicleSort, string> = {
  status: 'Status, then plate',
  plate: 'Plate A–Z',
  updated: 'Most recently updated',
  speed: 'Fastest first',
};

const STATUSES: LiveStatus[] = ['moving', 'idle', 'offline', 'alert'];

export const DRAWER_WIDTH = 356;

/**
 * The fleet panel. Persistent on desktop, a bottom sheet below `lg`, and always
 * the same list component so behaviour cannot diverge between the two.
 */
export function VehicleDrawer({ fleet }: { fleet: FleetSnapshot }) {
  const open = useUiStore((s) => s.drawerOpen);
  const setDrawerOpen = useUiStore((s) => s.setDrawerOpen);
  const toggleDrawer = useUiStore((s) => s.toggleDrawer);
  const selectedVehicleId = useUiStore((s) => s.selectedVehicleId);
  const selectVehicle = useUiStore((s) => s.selectVehicle);
  const search = useUiStore((s) => s.search);
  const setSearch = useUiStore((s) => s.setSearch);
  const sort = useUiStore((s) => s.sort);
  const setSort = useUiStore((s) => s.setSort);
  const groupFilter = useUiStore((s) => s.groupFilter);
  const setGroupFilter = useUiStore((s) => s.setGroupFilter);
  const statusFilter = useUiStore((s) => s.statusFilter);
  const toggleStatusFilter = useUiStore((s) => s.toggleStatusFilter);
  const clearFilters = useUiStore((s) => s.clearFilters);

  const filtered = statusFilter.length > 0 || groupFilter !== null || search.trim() !== '';

  return (
    <>
      <AnimatePresence initial={false}>
        {open && (
          <motion.aside
            key="drawer"
            initial={{ x: DRAWER_WIDTH, opacity: 0.4 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: DRAWER_WIDTH, opacity: 0.4 }}
            transition={{ duration: 0.26, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ width: DRAWER_WIDTH }}
            className="pointer-events-auto absolute inset-y-0 right-0 z-20 flex flex-col border-l bg-card max-lg:inset-x-0 max-lg:top-auto max-lg:h-[64%] max-lg:w-full max-lg:rounded-t-2xl max-lg:border-t max-lg:border-l-0 max-lg:shadow-float"
          >
            <header className="space-y-3 px-4 pt-4 pb-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight">All Vehicles</h2>
                  <p className="tnum text-[11.5px] text-muted-foreground">
                    {filtered
                      ? `${fleet.visible.length} of ${fleet.vehicles.length} shown`
                      : `${fleet.vehicles.length} in this fleet`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close panel"
                >
                  <X className="size-4" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search vehicles.."
                    className="h-10 rounded-xl bg-background pr-8 pl-9 text-[13px]"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute top-1/2 right-2.5 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'h-10 shrink-0 gap-1.5 rounded-xl px-3 text-[13px] font-medium',
                        filtered && 'border-primary/40 text-primary',
                      )}
                    >
                      <ListFilter className="size-4" />
                      Filters
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuLabel className="label-micro">Status</DropdownMenuLabel>
                    {STATUSES.map((status) => (
                      <DropdownMenuCheckboxItem
                        key={status}
                        checked={statusFilter.includes(status)}
                        onCheckedChange={() => toggleStatusFilter(status)}
                        onSelect={(event) => event.preventDefault()}
                      >
                        {STATUS_LABEL[status]}
                        <span className="tnum ml-auto text-[11px] text-muted-foreground">
                          {fleet.counts[status]}
                        </span>
                      </DropdownMenuCheckboxItem>
                    ))}

                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="label-micro flex items-center gap-1.5">
                      <ArrowUpDown className="size-3" /> Sort by
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={sort}
                      onValueChange={(value) => setSort(value as VehicleSort)}
                    >
                      {(Object.keys(SORT_LABEL) as VehicleSort[]).map((value) => (
                        <DropdownMenuRadioItem key={value} value={value}>
                          {SORT_LABEL[value]}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>

                    {fleet.groups.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="label-micro">Group</DropdownMenuLabel>
                        <DropdownMenuRadioGroup
                          value={groupFilter ?? '__all__'}
                          onValueChange={(value) =>
                            setGroupFilter(value === '__all__' ? null : value)
                          }
                        >
                          <DropdownMenuRadioItem value="__all__">All groups</DropdownMenuRadioItem>
                          {fleet.groups.map((group) => (
                            <DropdownMenuRadioItem key={group} value={group}>
                              {group}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </>
                    )}

                    {filtered && (
                      <>
                        <DropdownMenuSeparator />
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="w-full rounded-md px-2 py-1.5 text-left text-[13px] text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          Clear all filters
                        </button>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
              {fleet.isLoading ? (
                <div className="space-y-2.5">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton key={index} className="h-[92px] rounded-xl" />
                  ))}
                </div>
              ) : fleet.isError ? (
                // A failed fetch must not look like an empty fleet.
                <EmptyState
                  icon={WifiOff}
                  title="Could not load the fleet"
                  description="The vehicle list request failed. It will retry automatically; check the connection indicator in the top bar."
                  compact
                />
              ) : fleet.visible.length === 0 ? (
                <EmptyState
                  icon={Car}
                  title={fleet.vehicles.length === 0 ? 'No vehicles yet' : 'No vehicles match'}
                  description={
                    fleet.vehicles.length === 0
                      ? 'Add a vehicle and assign it a tracking device to see it here.'
                      : 'Try a different search term or clear the status filter.'
                  }
                  compact
                  action={
                    filtered ? (
                      <Button size="sm" variant="secondary" onClick={clearFilters}>
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <ul className="space-y-2.5">
                  <AnimatePresence initial={false}>
                    {fleet.visible.map((entry) => (
                      <li key={entry.vehicle.id}>
                        <VehicleRow
                          entry={entry}
                          selected={selectedVehicleId === entry.vehicle.id}
                          onSelect={() => selectVehicle(entry.vehicle.id, { fly: true })}
                        />
                      </li>
                    ))}
                  </AnimatePresence>
                </ul>
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {!open && (
        <div className="pointer-events-auto absolute top-4 right-4 z-20">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                className="h-10 gap-2 rounded-xl bg-card px-3.5 shadow-panel hover:bg-card"
                onClick={toggleDrawer}
              >
                <PanelRightOpen className="size-4" />
                <span className="tnum text-[13px] font-semibold">{fleet.vehicles.length}</span>
                <span className="text-[13px] font-medium">vehicles</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Show fleet panel</TooltipContent>
          </Tooltip>
        </div>
      )}
    </>
  );
}
